import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { NavigationFunction as RootNavigation, Pages } from "../../Navigation/root";
import { useTranslation } from "react-i18next";
import { colors, css, fonts, glyphs } from "../../Styles";
import {  EnhancedWidget, AutoReportModal } from "..";
import { ButtonTypes, ReportShortCut_Type } from "../../Definitions";
import { Graph_QL, Widget, AnimalInfo, System } from "../../Services";
import { AutoReport } from "../../Services/Agrisys_GqlDataCaller/WidgetCalls/GQLCalls/autoReportCall";
import NewButton from "../BaseComponents/newButton";
import AutoReportCard from "./SubComponents/autoReportCard ";
import { underFeedBalanceYesterday } from "../_Components";

interface AutoReportWidgetProps {
  widget: Widget;
  animals: AnimalInfo[];
  autoReports: AutoReport[];
  system?: System;
  connect: {
    connection: Graph_QL.Connection
    domain: string
  }
}
/**
 * AutoReportWidget
 * @param header - The title displayed at the top of the widget.
 * @param systemId - The ID of the system to fetch related data.
 * @returns A React component displaying automated reports and progress circles within an enhanced widget.
 */
export default function AutoReportWidget({ animals, widget, autoReports, system, connect }: AutoReportWidgetProps) {
  const { t } = useTranslation();
  const navigator = useNavigate();
  const [addAutoReportModal, setAddAutoReportModal] = useState<boolean>(false)
  const [dirtyWidget, setDirtyWidget] = useState(widget);

  useEffect(() => {
    setDirtyWidget(widget);
  }, [widget])
  
  const resetWidget = () => {
    setDirtyWidget(widget);
  };

  const autoReportData = useMemo(() => {
    if (autoReports) {
      return autoReports.filter((report) => dirtyWidget.auto_reports.includes(report.auto_report_id))
    }
    return []
  }, [autoReports, dirtyWidget.auto_reports]);

  return (
    <EnhancedWidget
      widget={dirtyWidget}
      changeData={setDirtyWidget}
      onCancel={resetWidget}
      overrides={{
        baseColor: colors.greyPrimary,
      }}
      oneContent={
        <div style={{ ...css.rightContentOuter }}>
          <div style={{ ...css.rightContentTop }}> 
            <p style={{ ...fonts.header, ...fonts.fontSize3, color: colors.greyLightTertiary}}>{t("widgets.autoReports.autoReports", { defaultValue: "Auto Reports" })}</p>
          </div>
          <div style={{ margin: "0px 0px 5px 0px", ...css.rightContentSeperator }} />
          <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", maxWidth: "630px", overflowX: "hidden", overflowY: "auto", padding: "10px", scrollbarColor: colors.greySecondary+colors.transparent, scrollbarWidth: "thin" }}>
            {autoReportData.map((report) => {
              const shouldCountAnimalsFilter = report.filter.filter((filter) => filter.key === "excess_remaining")
              return <div style={{ flex: "1 1 250px", width: "100%" }}>
                <AutoReportCard
                  key={report.auto_report_id}
                  autoReport={report}
                  onClick={() => RootNavigation({ Navigator: navigator, navigation_page: Pages.animals, navigation_tab: Pages.animals, replace: true, navArgs: { selected: system?.system_id ? { ...report, filter: [...report.filter, { key: "system_id", value: [{ id: system.system_id, label: "Navigated from system" }] }] } : report }, currentDomain: connect.domain })}
                  amount={Boolean(shouldCountAnimalsFilter.length) && shouldCountAnimalsFilter[0].value[0] ? underFeedBalanceYesterday(animals ?? [], shouldCountAnimalsFilter[0].value[0].id): undefined}
                />
              </div>
            })}
          </div> 
        </div>
      }
      editMode={{
        editHeader: true,
        editOneContent: (
          <div style={{ ...css.rightContentOuter }}>
            <div style={{ ...css.rightContentTop }}>
              <p style={{ ...fonts.header, ...fonts.fontSize3, color: colors.greyLightTertiary }}>{t("widgets.autoReports.autoReports", { defaultValue: "Auto Reports" })}</p>
            </div>
            <div style={{ margin: "0px 0px 5px 0px", ...css.rightContentSeperator }}/>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", maxWidth: "630px", overflowX: "hidden", overflowY: "auto", padding: "10px", scrollbarColor: colors.greySecondary+colors.transparent, scrollbarWidth: "thin" }}>
            {autoReportData.map((report) => {
              const shouldCountAnimalsFilter = report.filter.filter((filter) => filter.key === "excess_remaining")
              return <div style={{ flex: "1 1 250px", width: "100%" }}>
                <AutoReportCard
                  key={report.auto_report_id} 
                  autoReport={report}
                  onClick={() => {
                    setDirtyWidget({...dirtyWidget, auto_reports: dirtyWidget.auto_reports.filter((autoReportId) => autoReportId !== report.auto_report_id) });
                  }}
                  type={ReportShortCut_Type.delete}
                  amount={Boolean(shouldCountAnimalsFilter.length) && shouldCountAnimalsFilter[0].value[0] ? underFeedBalanceYesterday(animals ?? [], shouldCountAnimalsFilter[0].value[0].id): undefined}
                />
              </div>
            })}
          </div> 
            <div style={{ margin: "5px 0px 0px 0px", ...css.rightContentSeperator }}/>
            <div style={{ ...css.rightContentTop, alignItems: "center" }}>
              <NewButton
                content={""}
                buttonType={ButtonTypes.glyphButton}
                onClick={() => setAddAutoReportModal(true)}
                startEnhancer={glyphs.Add({})}
              />
            </div>
            { addAutoReportModal && <AutoReportModal connection={connect.connection} autoReports={autoReports} system={system} closeModal={() => setAddAutoReportModal(false)} widget={dirtyWidget} changeData={setDirtyWidget}/> }
          </div>
        )
      }}
    />
  );
}